import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { RedoCheckbox } from "@redotech/arbiter/checkbox/redo-checkbox";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { isAmazonEligibleZip } from "@redotech/redo-fulfillment-carrier-common/amazon-eligible-zips";
import { type AmazonEligibility } from "@redotech/redo-fulfillment-carrier-common/carrier-eligibility";
import type { RedoFulfillmentLocation } from "@redotech/redo-fulfillment-group-common/fulfillment-location";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import { rateTableCarrierLabel } from "@redotech/redo-fulfillment-rate-table-common/rate-table-mapping";
import { toast } from "@redotech/redo-web/alert";
import { memo, useContext, useMemo, useState } from "react";
import { AuthContext } from "../../../../../app/auth";
import { RedoAdminRpcClientContext } from "../../../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../../../team";
import { usersWhoCanEditRateTables } from "../../../outbound-labels";
import { useRateCalculator } from "../hooks";
import {
  type Address,
  type CarrierEligibility,
  type DhlEligibility,
  type RateCalcCarrier,
  type UniUniEligibility,
} from "../types";
import * as styles from "./carrier-selection-step.module.css";

const MAX_DHL_DISTANCE_MILES = 100;
const MAX_UNIUNI_DISTANCE_MILES = 300;

interface CarrierSelectionStepProps {
  teamId?: string | null;
}

export const CarrierSelectionStep = memo(function CarrierSelectionStep({
  teamId: teamIdProp,
}: CarrierSelectionStepProps) {
  const { state, dispatch } = useRateCalculator();
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const teamFromContext = useContext(TeamContext);
  const teamId = teamIdProp ?? teamFromContext?._id ?? null;
  const auth = useContext(AuthContext);
  const canOverrideEligibility =
    auth && usersWhoCanEditRateTables.includes(auth.email);

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const [address, setAddress] = useState<Address>({
    street1: state.merchantAddress?.street1 || "",
    city: state.merchantAddress?.city || "",
    state: state.merchantAddress?.state || "",
    postalCode: state.merchantAddress?.postalCode || "",
    country: state.merchantAddress?.country || "US",
  });

  const locationsLoad = useLoad(async () => {
    if (!teamId) {
      return [];
    }
    const result = await client.getTeamLocations({ teamId });
    return result.locations;
  }, [client, teamId]);

  const locationOptions: RedoListItem<RedoFulfillmentLocation>[] =
    useMemo(() => {
      if (locationsLoad.pending || !locationsLoad.value) {
        return [];
      }
      return locationsLoad.value
        .filter((loc) => loc.isActive && loc.address?.address1)
        .map((loc) => ({
          id: loc.externalId,
          type: "text" as const,
          text: `${loc.name} - ${loc.address.city || ""}, ${loc.address.provinceCode || loc.address.province || ""}`,
          value: loc,
        }));
    }, [locationsLoad]);

  const handleLocationSelect = useHandler(
    (item: RedoListItem<RedoFulfillmentLocation>) => {
      const loc = item.value;
      setSelectedLocationId(String(item.id));
      setAddress({
        street1: loc.address.address1 || "",
        city: loc.address.city || "",
        state: loc.address.provinceCode || loc.address.province || "",
        postalCode: loc.address.zip || "",
        country: loc.address.countryCode || loc.address.country || "US",
      });
    },
  );

  const handleCheckEligibility = useHandler(async () => {
    if (
      !address.street1 ||
      !address.city ||
      !address.state ||
      !address.postalCode
    ) {
      toast("Please fill in all address fields", { variant: "error" });
      return;
    }

    dispatch({ type: "SET_ELIGIBILITY_LOADING", loading: true });
    dispatch({ type: "SET_MERCHANT_ADDRESS", address });

    try {
      const result = await client.getCarrierDistributionCenterDistances({
        address: {
          street1: address.street1,
          city: address.city,
          state: address.state,
          postalCode: address.postalCode,
          country: address.country,
        },
      });

      let dhlEligibility: DhlEligibility | null = null;
      if (result.dhlDistances && result.dhlDistances.length > 0) {
        const sorted = [...result.dhlDistances].sort(
          (a, b) => a.distanceMiles - b.distanceMiles,
        );
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const nearest = sorted[0]!;
        dhlEligibility = {
          eligible: nearest.distanceMiles <= MAX_DHL_DISTANCE_MILES,
          nearestDC: nearest.distributionCenter,
          distanceMiles: nearest.distanceMiles,
          allDistances: result.dhlDistances,
        };
      }

      let uniuniEligibility: UniUniEligibility | null = null;
      if (result.uniuniDistances && result.uniuniDistances.length > 0) {
        const sorted = [...result.uniuniDistances].sort(
          (a, b) => a.distanceMiles - b.distanceMiles,
        );
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const nearest = sorted[0]!;
        uniuniEligibility = {
          eligible: nearest.distanceMiles <= MAX_UNIUNI_DISTANCE_MILES,
          nearestDC: nearest.distributionCenter,
          distanceMiles: nearest.distanceMiles,
          allDistances: result.uniuniDistances,
        };
      }

      const amazonEligibility: AmazonEligibility = {
        eligible: isAmazonEligibleZip(address.postalCode),
        zipEligible: isAmazonEligibleZip(address.postalCode),
      };

      const eligibility: CarrierEligibility = {
        dhl: dhlEligibility,
        amazon: amazonEligibility,
        ups: { eligible: true },
        usps: { eligible: true },
        uniuni: uniuniEligibility,
      };

      dispatch({ type: "SET_CARRIER_ELIGIBILITY", eligibility });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast(`Error checking eligibility: ${message}`, { variant: "error" });
    } finally {
      dispatch({ type: "SET_ELIGIBILITY_LOADING", loading: false });
    }
  });

  const handleToggleCarrier = useHandler((carrier: RateCalcCarrier) => {
    dispatch({ type: "TOGGLE_CARRIER", carrier });
  });

  const handleToggleOverride = useHandler((carrier: RateCalcCarrier) => {
    const wasOverridden = state.overriddenCarriers.includes(carrier);
    const isCurrentlySelected = state.selectedCarriers.includes(carrier);

    dispatch({ type: "TOGGLE_CARRIER_OVERRIDE", carrier });

    const shouldSelect = !wasOverridden && !isCurrentlySelected;
    const shouldDeselect = wasOverridden && isCurrentlySelected;

    if (shouldSelect || shouldDeselect) {
      dispatch({ type: "TOGGLE_CARRIER", carrier });
    }
  });

  const canProceed =
    state.carrierEligibility && state.selectedCarriers.length > 0;

  return (
    <Flex dir="column" gap="xl">
      <Flex dir="column" gap="sm">
        <RedoTextInput
          label="Session name"
          placeholder="e.g. Acme Corp - Q1 2026"
          setValue={(v) => dispatch({ type: "SET_SESSION_NAME", name: v })}
          value={state.sessionName}
        />
      </Flex>

      <Flex dir="column" gap="sm">
        <Text fontSize="lg" fontWeight="semibold">
          Merchant Address
        </Text>
      </Flex>

      <Flex gap="lg">
        <Flex dir="column" gap="md" grow={1}>
          {locationOptions.length > 0 && (
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium">
                Select Location
              </Text>
              <RedoSingleSelectDropdownInput<RedoFulfillmentLocation>
                options={locationOptions}
                optionSelected={({ item }) => handleLocationSelect(item)}
                placeholder="Choose a location..."
                selectedItem={locationOptions.find(
                  (o) => o.id === selectedLocationId,
                )}
                size="sm"
              />
            </Flex>
          )}

          {locationsLoad.pending && (
            <Text fontSize="sm" textColor="secondary">
              Loading locations...
            </Text>
          )}

          <RedoTextInput
            label="Street address"
            setValue={(v) => setAddress((a) => ({ ...a, street1: v }))}
            value={address.street1}
          />
          <Flex gap="md">
            <Flex grow={2}>
              <RedoTextInput
                label="City"
                setValue={(v) => setAddress((a) => ({ ...a, city: v }))}
                value={address.city}
              />
            </Flex>
            <Flex grow={1}>
              <RedoTextInput
                label="State"
                setValue={(v) => setAddress((a) => ({ ...a, state: v }))}
                value={address.state}
              />
            </Flex>
            <Flex grow={1}>
              <RedoTextInput
                label="ZIP"
                setValue={(v) => setAddress((a) => ({ ...a, postalCode: v }))}
                value={address.postalCode}
              />
            </Flex>
          </Flex>
          <Flex>
            <RedoButton
              hierarchy="secondary"
              onClick={handleCheckEligibility}
              pending={state.eligibilityLoading}
              size="md"
              text="Check eligibility"
            />
          </Flex>
        </Flex>

        <Flex
          borderColor="primary"
          borderRadius="lg"
          borderWidth="1px"
          dir="column"
          gap="md"
          grow={1}
          p="lg"
        >
          <Text fontSize="md" fontWeight="semibold">
            Carrier Selection
          </Text>

          {!state.carrierEligibility ? (
            <Text fontSize="sm" textColor="secondary">
              Enter an address and check eligibility to see available carriers.
            </Text>
          ) : (
            <Flex dir="column" gap="sm">
              <CarrierCheckbox
                canOverride={canOverrideEligibility}
                carrier={RateTableCarrier.DHL_ECS}
                eligibility={state.carrierEligibility.dhl}
                isOverridden={state.overriddenCarriers.includes(
                  RateTableCarrier.DHL_ECS,
                )}
                isSelected={state.selectedCarriers.includes(
                  RateTableCarrier.DHL_ECS,
                )}
                onToggle={() => handleToggleCarrier(RateTableCarrier.DHL_ECS)}
                onToggleOverride={() =>
                  handleToggleOverride(RateTableCarrier.DHL_ECS)
                }
              />
              <CarrierCheckbox
                canOverride={canOverrideEligibility}
                carrier={RateTableCarrier.Amazon}
                eligibility={state.carrierEligibility.amazon}
                isOverridden={state.overriddenCarriers.includes(
                  RateTableCarrier.Amazon,
                )}
                isSelected={state.selectedCarriers.includes(
                  RateTableCarrier.Amazon,
                )}
                onToggle={() => handleToggleCarrier(RateTableCarrier.Amazon)}
                onToggleOverride={() =>
                  handleToggleOverride(RateTableCarrier.Amazon)
                }
              />
              <CarrierCheckbox
                canOverride={canOverrideEligibility}
                carrier={RateTableCarrier.UPS}
                eligibility={state.carrierEligibility.ups}
                isOverridden={state.overriddenCarriers.includes(
                  RateTableCarrier.UPS,
                )}
                isSelected={state.selectedCarriers.includes(
                  RateTableCarrier.UPS,
                )}
                onToggle={() => handleToggleCarrier(RateTableCarrier.UPS)}
                onToggleOverride={() =>
                  handleToggleOverride(RateTableCarrier.UPS)
                }
              />
              <CarrierCheckbox
                canOverride={canOverrideEligibility}
                carrier={RateTableCarrier.USPS}
                eligibility={state.carrierEligibility.usps}
                isOverridden={state.overriddenCarriers.includes(
                  RateTableCarrier.USPS,
                )}
                isSelected={state.selectedCarriers.includes(
                  RateTableCarrier.USPS,
                )}
                onToggle={() => handleToggleCarrier(RateTableCarrier.USPS)}
                onToggleOverride={() =>
                  handleToggleOverride(RateTableCarrier.USPS)
                }
              />
              <CarrierCheckbox
                canOverride={canOverrideEligibility}
                carrier={RateTableCarrier.UNIUNI}
                eligibility={state.carrierEligibility.uniuni}
                isOverridden={state.overriddenCarriers.includes(
                  RateTableCarrier.UNIUNI,
                )}
                isSelected={state.selectedCarriers.includes(
                  RateTableCarrier.UNIUNI,
                )}
                onToggle={() => handleToggleCarrier(RateTableCarrier.UNIUNI)}
                onToggleOverride={() =>
                  handleToggleOverride(RateTableCarrier.UNIUNI)
                }
              />
            </Flex>
          )}
        </Flex>
      </Flex>

      {(state.carrierEligibility?.dhl || state.carrierEligibility?.uniuni) && (
        <Flex gap="md" wrap="wrap">
          {state.carrierEligibility.dhl && (
            <Flex flex={1}>
              <DhlEligibilityDetails
                eligibility={state.carrierEligibility.dhl}
              />
            </Flex>
          )}
          {state.carrierEligibility.uniuni && (
            <Flex flex={1}>
              <UniUniEligibilityDetails
                eligibility={state.carrierEligibility.uniuni}
              />
            </Flex>
          )}
        </Flex>
      )}

      <Flex gap="md" justify="flex-end">
        <RedoButton
          disabled={!canProceed}
          hierarchy="primary"
          onClick={() => dispatch({ type: "SET_STEP", step: 2 })}
          size="md"
          text="Continue"
        />
      </Flex>
    </Flex>
  );
});

const CarrierCheckbox = memo(function CarrierCheckbox({
  carrier,
  eligibility,
  isSelected,
  onToggle,
  canOverride,
  isOverridden,
  onToggleOverride,
}: {
  carrier: RateCalcCarrier;
  eligibility:
    | { eligible: boolean }
    | AmazonEligibility
    | DhlEligibility
    | UniUniEligibility
    | null;
  isSelected: boolean;
  onToggle: () => void;
  canOverride?: boolean;
  isOverridden?: boolean;
  onToggleOverride?: () => void;
}) {
  const isEligible =
    carrier === RateTableCarrier.Amazon &&
    eligibility &&
    "zipEligible" in eligibility
      ? (eligibility as AmazonEligibility).zipEligible
      : (eligibility?.eligible ?? false);

  const getIneligibilityReason = (): string => {
    if (
      carrier === RateTableCarrier.Amazon &&
      eligibility &&
      "zipEligible" in eligibility
    ) {
      const amazonElig = eligibility as AmazonEligibility;
      if (!amazonElig.zipEligible) {
        return "ZIP not in eligible area";
      }
    }
    if (
      carrier === RateTableCarrier.DHL_ECS &&
      eligibility &&
      "distanceMiles" in eligibility
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const dhlElig = eligibility as DhlEligibility;
      if (!dhlElig.eligible) {
        return `Too far from DC (${dhlElig.distanceMiles.toFixed(0)}mi)`;
      }
    }
    if (
      carrier === RateTableCarrier.UNIUNI &&
      eligibility &&
      "distanceMiles" in eligibility
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const uniUniElig = eligibility as UniUniEligibility;
      if (!uniUniElig.eligible) {
        return `Too far from DC (${uniUniElig.distanceMiles.toFixed(0)}mi)`;
      }
    }
    return "Not eligible";
  };

  const effectiveEligibility = isEligible || isOverridden;

  return (
    <Flex dir="column" gap="xs">
      <Flex align="center" gap="md">
        <RedoCheckbox
          disabled={!effectiveEligibility}
          setValue={onToggle}
          value={isSelected && !!effectiveEligibility}
        />
        <Flex align="center" gap="sm" grow={1}>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textColor={effectiveEligibility ? "primary" : "disabled"}
          >
            {rateTableCarrierLabel(carrier)}
          </Text>
          {!isEligible && !isOverridden && (
            <Text fontSize="xs" textColor="secondary">
              ({getIneligibilityReason()})
            </Text>
          )}
          {isOverridden && (
            <Text fontSize="xs" fontWeight="semibold" textColor="warning">
              (eligibility overridden)
            </Text>
          )}
        </Flex>
      </Flex>
      {canOverride && !isEligible && (
        <Flex align="center" gap="sm" pl="3xl">
          <RedoCheckbox
            setValue={onToggleOverride ?? (() => {})}
            value={isOverridden ?? false}
          />
          <Text fontSize="xs" textColor="secondary">
            Override eligibility check
          </Text>
        </Flex>
      )}
    </Flex>
  );
});

const UniUniEligibilityDetails = memo(function UniUniEligibilityDetails({
  eligibility,
}: {
  eligibility: UniUniEligibility;
}) {
  return (
    <Flex
      borderColor="primary"
      borderRadius="lg"
      borderWidth="1px"
      className={
        eligibility.eligible
          ? styles.eligibilityCardEligible
          : styles.eligibilityCardNotEligible
      }
      dir="column"
      gap="sm"
      p="md"
    >
      <Text fontSize="sm" fontWeight="semibold">
        Uni Uni Details
      </Text>
      <Flex gap="xl">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Nearest DC
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            {eligibility.nearestDC}
          </Text>
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Distance
          </Text>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textColor={
              eligibility.distanceMiles <= MAX_UNIUNI_DISTANCE_MILES
                ? "primary"
                : "warning"
            }
          >
            {eligibility.distanceMiles.toFixed(1)} mi
          </Text>
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Status
          </Text>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textColor={eligibility.eligible ? "success" : "warning"}
          >
            {eligibility.eligible
              ? "Eligible"
              : `Too far (>${MAX_UNIUNI_DISTANCE_MILES} mi)`}
          </Text>
        </Flex>
      </Flex>

      {eligibility.allDistances.length > 1 && (
        <Flex dir="column" gap="xs" pt="sm">
          <Text fontSize="xs" textColor="secondary">
            All DCs
          </Text>
          <Flex gap="md" wrap="wrap">
            {eligibility.allDistances
              .sort((a, b) => a.distanceMiles - b.distanceMiles)
              .slice(0, 5)
              .map((dc) => (
                <Text
                  fontSize="xs"
                  key={dc.distributionCenter}
                  textColor={
                    dc.distanceMiles <= MAX_UNIUNI_DISTANCE_MILES
                      ? "success"
                      : "secondary"
                  }
                >
                  {dc.distributionCenter}: {dc.distanceMiles.toFixed(0)}mi
                </Text>
              ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});

const DhlEligibilityDetails = memo(function DhlEligibilityDetails({
  eligibility,
}: {
  eligibility: DhlEligibility;
}) {
  return (
    <Flex
      borderColor="primary"
      borderRadius="lg"
      borderWidth="1px"
      className={
        eligibility.eligible
          ? styles.eligibilityCardEligible
          : styles.eligibilityCardNotEligible
      }
      dir="column"
      gap="sm"
      p="md"
    >
      <Text fontSize="sm" fontWeight="semibold">
        DHL ECS Details
      </Text>
      <Flex gap="xl">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Nearest DC
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            {eligibility.nearestDC}
          </Text>
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Distance
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            {eligibility.distanceMiles.toFixed(1)} miles
          </Text>
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Status
          </Text>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textColor={eligibility.eligible ? "success" : "warning"}
          >
            {eligibility.eligible
              ? "Eligible"
              : `Too far (>${MAX_DHL_DISTANCE_MILES} mi)`}
          </Text>
        </Flex>
      </Flex>

      {eligibility.allDistances.length > 1 && (
        <Flex dir="column" gap="xs" pt="sm">
          <Text fontSize="xs" textColor="secondary">
            All DCs
          </Text>
          <Flex gap="md" wrap="wrap">
            {eligibility.allDistances
              .sort((a, b) => a.distanceMiles - b.distanceMiles)
              .slice(0, 5)
              .map((dc) => (
                <Text
                  fontSize="xs"
                  key={dc.distributionCenter}
                  textColor={
                    dc.distanceMiles <= MAX_DHL_DISTANCE_MILES
                      ? "success"
                      : "secondary"
                  }
                >
                  {dc.distributionCenter}: {dc.distanceMiles.toFixed(0)}mi
                </Text>
              ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
